import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Download, Loader2, CheckCircle, XCircle, CreditCard } from 'lucide-react';
import { notify } from '@/stores/notificationStore';
import { publicInvoicesAPI } from '@/utils/api';
import { logger } from '@/utils/logger';
import { InvoiceTemplateClassic } from '@/components/templates/invoice/InvoiceTemplateClassic';
import { InvoiceTemplateModern } from '@/components/templates/invoice/InvoiceTemplateModern';
import { InvoiceTemplateCorporate } from '@/components/templates/invoice/InvoiceTemplateCorporate';
import { InvoiceTemplateElegant } from '@/components/templates/invoice/InvoiceTemplateElegant';
import type { InvoiceData, InvoiceItem, TemplateSettings } from '@/components/templates/types';

interface PublicInvoiceViewProps {}

/**
 * Mappe les données API publique vers InvoiceData (format attendu par les templates facture)
 */
function mapToInvoiceData(invoice: any): InvoiceData {
  const rawItems = typeof invoice.items === 'string'
    ? JSON.parse(invoice.items)
    : invoice.items || [];

  const items: InvoiceItem[] = rawItems.map((item: any) => ({
    description: item.description || '',
    unitPrice: Number(item.unitPrice) || 0,
    quantity: Number(item.quantity) || 0,
    total: Number(item.totalHT) || 0,
    totalHT: Number(item.totalHT) || 0,
    vatRate: Number(item.taxPercent) || 0,
    taxPercent: Number(item.taxPercent) || 0,
    vatAmount: (Number(item.totalHT) || 0) * ((Number(item.taxPercent) || 0) / 100),
  }));

  const invoiceData: any = {
    id: invoice.id,
    invoiceNumber: invoice.number || '',
    issueDate: invoice.issueDate || '',
    date: invoice.issueDate || '',
    dueDate: invoice.dueDate || '',
    title: invoice.title || '',
    clientName: invoice.Client?.fullName || '',
    clientCompany: invoice.Client?.companyName || '',
    clientAddressLine1: invoice.Client?.addressLine1 || invoice.Client?.address || '',
    clientCity: invoice.Client?.city || '',
    clientZipCode: invoice.Client?.zipCode || invoice.Client?.postalCode || '',
    clientCountryCode: invoice.Client?.countryCode || invoice.Client?.country || '',
    clientPhone: invoice.Client?.phone || '',
    clientEmail: invoice.Client?.email || '',
    // Objet Client imbriqué (utilisé par ClientInfoSection)
    Client: invoice.Client || null,
    items,
    subtotal: parseFloat(String(invoice.totalHT)) || 0,
    totalHT: parseFloat(String(invoice.totalHT)) || 0,
    tax: parseFloat(String(invoice.totalTVA)) || 0,
    totalTVA: parseFloat(String(invoice.totalTVA)) || 0,
    total: parseFloat(String(invoice.totalTTC)) || 0,
    totalTTC: parseFloat(String(invoice.totalTTC)) || 0,
    discountType: invoice.discountType,
    discountValue: invoice.discountValue ? parseFloat(invoice.discountValue) : undefined,
    discountAmount: invoice.discountAmount ? parseFloat(String(invoice.discountAmount)) : undefined,
    notes: invoice.notes || '',
    internalNotes: invoice.internalNotes || '',
    termsConditions: invoice.termsConditions || '',
    status: invoice.status,
    paidAmount: parseFloat(String(invoice.paidAmount)) || 0,
    remainingAmount: parseFloat(String(invoice.remainingAmount)) || 0,
    purchaseOrderNumber: invoice.purchaseOrderNumber || '',
    reference: invoice.reference || '',
  };

  return invoiceData;
}

/**
 * Construit un objet Company compatible avec le template system
 */
function buildCompanyFromPublicData(invoice: any): any {
  const c = invoice.Company;
  if (!c) return null;

  return {
    id: c.id,
    tradeName: c.tradeName || c.legalName || '',
    legalName: c.legalName || '',
    logoUrl: c.logoUrl,
    addressLine1: c.addressLine1 || c.address || '',
    city: c.city || '',
    zipCode: c.zipCode || c.postalCode || '',
    countryCode: c.countryCode || c.country || 'FR',
    phone: c.phone || '',
    email: c.email || '',
    registrationNumber: c.registrationNumber || '',
    vatNumber: c.vatNumber || '',
    vatRegime: c.vatRegime,
    vatExemptArticle: c.vatExemptArticle,
    legalForm: c.legalForm,
    capital: c.capital,
    rcsCity: c.rcsCity,
    rcsNumber: c.rcsNumber,
    apeCode: c.apeCode,
    iban: c.iban,
    bic: c.bic,
    legalNotesDefault: c.legalNotesDefault || '',
    defaultPaymentTermsDays: c.defaultPaymentTermsDays,
  };
}

/**
 * Construit les TemplateSettings à partir de la réponse API publique
 */
function buildTemplateSettings(invoice: any): TemplateSettings | undefined {
  const tpl = invoice.template;
  if (!tpl) return undefined;

  return {
    id: tpl.id,
    name: tpl.name,
    displayName: tpl.displayName,
    styleConfig: typeof tpl.styleConfig === 'string'
      ? JSON.parse(tpl.styleConfig)
      : tpl.styleConfig,
    layoutConfig: typeof tpl.layoutConfig === 'string'
      ? JSON.parse(tpl.layoutConfig)
      : tpl.layoutConfig,
  };
}

/**
 * Détermine la couleur primaire.
 * Priorité: 1) colorPrimary de templatePreference  2) styleConfig du template  3) fallback teal
 */
function resolveColor(invoice: any): string {
  const pref = invoice.templatePreference;
  if (pref?.colorPrimary) {
    return pref.colorPrimary;
  }
  const tpl = invoice.template;
  if (tpl?.styleConfig) {
    const styleConfig = typeof tpl.styleConfig === 'string'
      ? JSON.parse(tpl.styleConfig)
      : tpl.styleConfig;
    if (styleConfig?.primaryColor) return styleConfig.primaryColor;
  }
  return '#0D9488';
}

/**
 * Résout le nom du template pour le routing
 */
function resolveTemplateName(invoice: any): string {
  return invoice.template?.displayName?.toLowerCase() || 'classic';
}

export const PublicInvoiceView: React.FC<PublicInvoiceViewProps> = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [invoice, setInvoice] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPaying, setIsPaying] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentCancelled, setPaymentCancelled] = useState(false);
  const [stripeConnectEnabled, setStripeConnectEnabled] = useState(false);
  const [paypalEnabled, setPaypalEnabled] = useState(false);
  const [paypalProcessing, setPaypalProcessing] = useState(false);

  // Charger les options de paiement depuis l'endpoint dédié
  useEffect(() => {
    if (!token) return;

    const fetchPaymentOptions = async () => {
      try {
        const data = await publicInvoicesAPI.getPaymentOptions(token);
        if (data.success && data.data?.paymentMethods) {
          setStripeConnectEnabled(!!data.data.paymentMethods.stripe?.enabled);
          setPaypalEnabled(!!data.data.paymentMethods.paypal?.enabled);
        }
      } catch (err) {
        // Silencieux : pas de paiement disponible n'est pas une erreur critique
        logger.debug('Payment options not available', err);
      }
    };

    fetchPaymentOptions();
  }, [token]);

  // Vérifier le statut du paiement dans les paramètres URL
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');

    if (paymentStatus === 'success') {
      setPaymentSuccess(true);
      notify.success('Paiement effectué avec succès !', 'Paiement');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (paymentStatus === 'cancelled') {
      setPaymentCancelled(true);
      notify.warning('Paiement annulé', 'Paiement');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [searchParams]);

  // Charger la facture depuis l'API publique
  useEffect(() => {
    if (!token) return;

    const fetchInvoice = async () => {
      try {
        setLoading(true);
        const data = await publicInvoicesAPI.getInvoice(token);
        if (data.success) {
          setInvoice(data.data);
          if (data.data.status === 'paid' || data.data.paymentStatus === 'succeeded') {
            setPaymentSuccess(true);
          }
        } else {
          throw new Error(data.message || 'Erreur lors du chargement de la facture');
        }
      } catch (err) {
        logger.error('Erreur:', err);
        setError((err as Error).message);
        notify.error((err as Error).message, 'Erreur');
      } finally {
        setLoading(false);
      }
    };

    fetchInvoice();
  }, [token]);

  // Gérer le paiement via Stripe Checkout
  const handlePayment = async () => {
    if (!token || !invoice) return;

    setIsPaying(true);
    try {
      const data = await publicInvoicesAPI.createCheckoutSession(token);
      if (data.success && data.data.url) {
        window.location.href = data.data.url;
      } else {
        throw new Error(data.message || 'Erreur lors de la création de la session de paiement');
      }
    } catch (err) {
      logger.error('Erreur paiement:', err);
      notify.error((err as Error).message, 'Erreur');
      setIsPaying(false);
    }
  };

  // Gérer le paiement via PayPal
  const handlePayPalPayment = async () => {
    if (!token || !invoice) return;

    setPaypalProcessing(true);
    try {
      const data = await publicInvoicesAPI.createPayPalOrder(token);
      if (data.success && data.data.approvalUrl) {
        window.location.href = data.data.approvalUrl;
      } else {
        throw new Error(data.message || 'Erreur lors de la création de la commande PayPal');
      }
    } catch (err) {
      logger.error('Erreur paiement PayPal:', err);
      notify.error((err as Error).message, 'Erreur');
      setPaypalProcessing(false);
    }
  };

  // Télécharger le PDF Factur-X
  const handleDownloadPDF = async () => {
    if (!invoice) return;

    try {
      const company = {
        name: invoice.Company?.tradeName || invoice.Company?.legalName,
        address: invoice.Company?.addressLine1 || invoice.Company?.address,
        city: invoice.Company?.city,
        zipCode: invoice.Company?.zipCode || invoice.Company?.postalCode,
        country: invoice.Company?.countryCode || invoice.Company?.country || 'FR',
        phone: invoice.Company?.phone,
        email: invoice.Company?.email,
        siret: invoice.Company?.registrationNumber,
        vatNumber: invoice.Company?.vatNumber,
        legalForm: invoice.Company?.legalForm,
        capital: invoice.Company?.capital,
        rcsCity: invoice.Company?.rcsCity,
        rcsNumber: invoice.Company?.rcsNumber,
        apeCode: invoice.Company?.apeCode,
        iban: invoice.Company?.iban,
        bic: invoice.Company?.bic,
        logoUrl: invoice.Company?.logoUrl,
        quoteColor: resolveColor(invoice),
      };

      const { downloadFacturXPDF, convertInvoiceToFacturX } = await import('@/utils/facturx');
      const facturxData = convertInvoiceToFacturX(invoice, company);
      await downloadFacturXPDF(facturxData);
      notify.success('PDF téléchargé avec succès', 'Téléchargement');
    } catch (error) {
      logger.error('Erreur génération PDF:', error);
      notify.error('Erreur lors de la génération du PDF', 'Erreur');
    }
  };

  // Affichage pendant le chargement
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-teal-600 mx-auto mb-4" />
          <p className="text-gray-600">{t('invoices.public.loading')}</p>
        </div>
      </div>
    );
  }

  // Affichage en cas d'erreur
  if (error || !invoice) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <XCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('invoices.public.error')}</h2>
          <p className="text-gray-600 mb-6">{error || 'Facture introuvable'}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-gradient-to-r from-teal-600 to-purple-600 hover:from-teal-700 hover:to-purple-700 text-white rounded-lg transition-colors"
          >
            {t('invoices.public.backToHome')}
          </button>
        </div>
      </div>
    );
  }

  // Mapper vers InvoiceData pour les vrais templates facture
  const invoiceData = mapToInvoiceData(invoice);
  const companyData = buildCompanyFromPublicData(invoice);
  const templateSettings = buildTemplateSettings(invoice);
  const primaryColor = resolveColor(invoice);
  const templateName = resolveTemplateName(invoice);
  const total = invoiceData.total;

  // Props communes pour tous les templates
  const templateProps = {
    invoiceData,
    onClose: () => { /* Page publique : pas de close */ },
    template: templateSettings,
    company: companyData,
    color: primaryColor,
  };

  // Sélection du bon template selon le displayName (même logique que InvoiceTemplateRouter)
  const renderTemplate = () => {
    switch (templateName) {
      case 'modern':
        return <InvoiceTemplateModern {...templateProps} />;
      case 'corporate':
        return <InvoiceTemplateCorporate {...templateProps} />;
      case 'elegant':
        return <InvoiceTemplateElegant {...templateProps} />;
      case 'classic':
      default:
        return <InvoiceTemplateClassic {...templateProps} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Masquer l'ActionBar du template (fixed bottom-6 right-6 z-50) */}
      <style>{`.public-invoice-wrapper .fixed.bottom-6.right-6.z-50 { display: none !important; }`}</style>

      {/* Header public : téléchargement PDF + paiement */}
      <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-lg border-b border-gray-200 print:hidden">
        <div className="max-w-4xl mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {invoice.Company?.logoUrl && (
              <img
                src={invoice.Company.logoUrl}
                alt={invoice.Company.tradeName || invoice.Company.legalName || ''}
                className="h-8 object-contain"
              />
            )}
            <span className="text-sm font-semibold text-gray-700">
              {invoiceData.invoiceNumber}
            </span>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={handleDownloadPDF}
              className="flex items-center gap-2 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              style={{ backgroundColor: primaryColor }}
            >
              <Download className="w-4 h-4" />
              {t('invoices.public.downloadPdf')}
            </button>

            {invoice.status !== 'paid' && stripeConnectEnabled && (
              <button
                onClick={handlePayment}
                disabled={isPaying || paypalProcessing}
                className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-green-700 hover:from-green-600 hover:to-green-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isPaying ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CreditCard className="w-4 h-4" />
                )}
                Payer ({total.toFixed(2)} €)
              </button>
            )}

            {invoice.status !== 'paid' && paypalEnabled && (
              <button
                onClick={handlePayPalPayment}
                disabled={isPaying || paypalProcessing}
                className="flex items-center gap-2 bg-[#0070ba] hover:bg-[#003087] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {paypalProcessing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z"/>
                  </svg>
                )}
                PayPal ({total.toFixed(2)} €)
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Message quand aucun moyen de paiement n'est configuré */}
      {invoice.status !== 'paid' && !stripeConnectEnabled && !paypalEnabled && !paymentSuccess && (
        <div className="max-w-4xl mx-auto pt-6 px-4 print:hidden">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <p className="text-sm text-blue-800 font-medium mb-2">
              {t('invoices.public.bankTransfer')}
            </p>
            <p className="text-xs text-blue-700">
              Le paiement en ligne n'est pas encore activé pour cette facture.
              Veuillez contacter {invoice.Company?.tradeName || invoice.Company?.legalName || 'l\'émetteur'} pour les modalités de paiement.
            </p>
            {invoice.Company?.email && (
              <a
                href={`mailto:${invoice.Company.email}`}
                className="inline-flex items-center gap-2 mt-3 text-sm text-blue-700 hover:text-blue-900 font-medium"
              >
                {t('invoices.public.contactByEmail')}
              </a>
            )}
          </div>
        </div>
      )}

      {/* Message de succès après paiement */}
      {paymentSuccess && (
        <div className="max-w-4xl mx-auto pt-6 px-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 flex items-center gap-4">
            <CheckCircle className="w-12 h-12 text-green-600 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-semibold text-green-800 mb-1">
                {t('invoices.public.paymentSuccess')}
              </h3>
              <p className="text-green-700">
                Votre paiement a été enregistré. Vous recevrez une confirmation par email.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Message d'annulation du paiement */}
      {paymentCancelled && !paymentSuccess && (
        <div className="max-w-4xl mx-auto pt-6 px-4">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 flex items-center gap-4">
            <XCircle className="w-12 h-12 text-orange-600 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-semibold text-orange-800 mb-1">
                {t('invoices.public.paymentCancelled')}
              </h3>
              <p className="text-orange-700">
                Le paiement a été annulé. Vous pouvez réessayer en cliquant sur le bouton "Payer maintenant".
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Rendu du document via le vrai template facture (Classic/Modern/Corporate/Elegant) */}
      <div className="public-invoice-wrapper">
        {renderTemplate()}
      </div>
    </div>
  );
};
